import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    Paper,
    Tabs,
    Tab,
    LinearProgress,
    Avatar,
    Chip,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CircularProgress,
} from "@mui/material";
import {
    BookOpen,
    FileText,
    HelpCircle,
    MessageSquare,
    Users,
    Calendar,
    Clock,
    ChevronRight,
    MoreVertical,
    PlayCircle,
    Download,
    CheckCircle2,
    Lock,
    ArrowLeft,
    Settings,
    Edit2,
    Info,
    ExternalLink,
    Plus,
    X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyCourse } from "../../../../Redux/Slices/CourseDetail/MyCourseSlice";
import backgroundImage from "../../assets/Vector 17.svg";

const CourseDetail = () => {
    const [tabValue, setTabValue] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    
    // Get course_id from URL path - handles patterns like /courses/details/{course_id}
    const pathSegments = location.pathname.split('/');
    const detailsIndex = pathSegments.indexOf('details');
    
    // Extract from state.course.course_id first, then state.course_id, then path segments
    let extractedId = location.state?.course?.course_id || location.state?.course_id;
    if (!extractedId) {
        if (detailsIndex !== -1 && pathSegments.length > detailsIndex + 1) {
            extractedId = pathSegments[detailsIndex + 1];
        } else {
            const lastSegment = pathSegments[pathSegments.length - 1];
            if (lastSegment !== 'detail' && lastSegment !== 'courses') {
                extractedId = lastSegment;
            }
        }
    }
    const courseId = extractedId;
    
    // Debug logging
    console.log('Current pathname:', location.pathname);
    console.log('Path segments:', pathSegments);
    console.log('Details index:', detailsIndex);
    console.log('Extracted course_id:', courseId);
    
    // Fetch course data from Redux
    const myCourseResult = useSelector((state: any) => state.MyCourseSlice || { data: null, isLoading: false });
    const courseData = myCourseResult?.data?.data;
    const isLoading = myCourseResult?.isLoading;
    
    // Fetch course data on component mount
    useEffect(() => {
        if (courseId) {
            dispatch(fetchMyCourse(courseId));
        }
    }, [dispatch, courseId]);
    
    // Fallback to location state for backward compatibility
    const course = courseData || location.state?.course;

    const courseInfo = {
        title: course?.full_name || course?.title || "Advanced Algebra & Calculus",
        tutor: course?.tutor || "Dr. John Smith",
        progress: course?.progress || 0,
        description: course?.description || "Master the complexities of modern calculus and algebraic structures. This course covers everything from limits to differential equations.",
        category: course?.course_category?.name || course?.category || "Mathematics & Logic",
        duration: course?.duration || 13,
        totalSubjects: course?.subjects?.length || course?.lessons || 3,
        totalMembers: course?.total_enrolled || course?.students || 1205,
        image: course?.image || "/course-placeholder.jpg",
        yeargroup: course?.yeargroup_country_map?.[0]?.year_group_data?.year_group || "Year 12",
        region: course?.yeargroup_country_map?.[0]?.region?.region || "Global",
        state: course?.yeargroup_country_map?.[0]?.region?.state || "",
    };
    
    // Show loading state while fetching data
    if (isLoading && !courseData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const subjects = [
        {
            id: "1",
            name: "Mathematics",
            topics: [
                { name: "Complex Numbers", duration: "2 weeks", subtopics: ["Imaginary Unit", "Conjugates"] },
                { name: "Roots of Unity", duration: "1 week", subtopics: ["De Moivre's Theorem"] }
            ],
            startDate: "2024-01-15",
            endDate: "2024-02-15",
            references: {
                studyNotes: "Calculus Fundamentals",
                booklet: "Module 1 Booklet",
                videoTutorials: "Concept Summary"
            }
        },
    ];

    const courseTopics = [
        {
            id: 1,
            name: "Complex Numbers & Roots",
            completed: true,
            activities: [
                { type: "pdf", title: "Intro to Complex Planes", duration: "1.2 MB", status: "completed" },
                { type: "quiz", title: "Practice Quiz: De Moivre's Theorem", duration: "15 questions", status: "completed" },
                { type: "video", title: "Polar Form Deep Dive", duration: "18:45", status: "completed" },
            ]
        },
        {
            id: 2,
            name: "Calculus: Differentiation",
            completed: true,
            activities: [
                { type: "video", title: "Chain Rule Mastery", duration: "25:10", status: "completed" },
                { type: "assignment", title: "Weekly Problem Set - Level 2", duration: "Due Jan 28", status: "submitted" },
                { type: "pdf", title: "Differentiation Cheat Sheet", duration: "450 KB", status: "completed" },
            ]
        },
        {
            id: 3,
            name: "Calculus: Integration Techniques",
            completed: false,
            activities: [
                { type: "video", title: "Integration by Parts", duration: "22:00", status: "in-progress" },
                { type: "assignment", title: "Integration Challenge", duration: "Due Feb 04", status: "pending" },
                { type: "quiz", title: "Advanced Integration Quiz", duration: "10 questions", status: "locked" },
            ]
        },
    ];

    const getActivityIcon = (type: string) => {
        switch (type) {
            case "video": return <PlayCircle size={20} color="#4f46e5" />;
            case "assignment": return <FileText size={20} color="#06b6d4" />;
            case "quiz": return <HelpCircle size={20} color="#f59e0b" />;
            case "pdf": return <Download size={20} color="#10b981" />;
            default: return <BookOpen size={20} />;
        }
    };

    return (
        <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Button
                        startIcon={<ArrowLeft size={18} />}
                        onClick={() => navigate("/online-tutoring/courses")}
                        sx={{ textTransform: "none", color: "#101010ff", fontWeight: 600, fontSize:"14px" }}
                    >
                        Back
                    </Button>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, md: 3 },
                        py: { xs: 2, md: 2.5 },
                        borderRadius: "32px",
                        position: "relative",
                        overflow: "hidden",
                        mb: 3,
                        background: "linear-gradient(90deg, #009DFF, #017CC9)",
                        color: "#fff",
                        boxShadow: "0 20px 40px rgba(30, 64, 175, 0.15)",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            right: -50,
                            bottom: -20,
                            width: "50%",
                            height: "100%",
                            opacity: 0.15,
                            zIndex: 0,
                            pointerEvents: "none",
                            "& img": {
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                transform: "rotate(-5deg) scale(1.2)"
                            }
                        }}
                    >
                        <img src={backgroundImage} alt="" />
                    </Box>

                    <Box sx={{ position: "relative", zIndex: 1 }}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Chip
                                        label={courseInfo.category}
                                        sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#f9f9faff", fontWeight: 700, border: "1px solid rgba(255,255,255,0.1)" }}
                                    />
                                    <Chip
                                        label={courseInfo.yeargroup}
                                        sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#f9f9faff", fontWeight: 700, border: "1px solid rgba(255,255,255,0.1)" }}
                                    />
                                </Box>
                                <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                                    {courseInfo.title}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 1.5 }}>
                                    {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Avatar src="/tutor.jpg" sx={{ width: 32, height: 32 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{courseInfo.tutor}</Typography>
                                    </Box> */}
                                    <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
                                </Box>
                                <Typography variant="body1" sx={{ color: "#ffffffff", mb: 0, lineHeight: 1.6, maxWidth: "600px" }}>
                                    {courseInfo.description}
                                </Typography>
                            </Grid>
                            {/* <Grid item xs={12} md={4}>
                                <Box sx={{
                                    p: 2.5,
                                    background: "rgba(255,255,255,0.03)",
                                    borderRadius: "28px",
                                    backdropFilter: "blur(12px)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    position: "relative",
                                    zIndex: 1,
                                    boxShadow: "inset 0 0 30px rgba(255,255,255,0.05)"
                                }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "#94a3b8" }}>
                                        Course Progress
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5 }}>
                                        {courseInfo.progress}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={courseInfo.progress}
                                        sx={{
                                            height: 10,
                                            borderRadius: 5,
                                            bgcolor: "rgba(255,255,255,0.1)",
                                            "& .MuiLinearProgress-bar": { bgcolor: "#4f46e5", borderRadius: 5 }
                                        }}
                                    />
                                </Box>
                            </Grid> */}
                        </Grid>
                    </Box>
                </Paper>

                <Box sx={{ borderBottom: "1px solid #e2e8f0", mb: 4 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => {
                            setTabValue(v);
                            if (v !== 1) setIsEditing(false);
                        }}
                        sx={{
                            minHeight: "34px",
                            height: "34px",
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "14px",
                                color: "#64748b",
                                minHeight: "40px",
                                height: "40px",
                                padding: "4px 16px",
                                borderRadius: "8px"
                            },
                            "& .Mui-selected": { color: "#4f46e5 !important" },
                            "& .MuiTabs-indicator": { bgcolor: "#4f46e5", height: 2, borderRadius: "2px 2px 0 0" }
                        }}
                    >
                        <Tab label="Course Details" icon={<Info size={18} />} iconPosition="start" />
                        <Tab label="Subject" icon={<BookOpen size={18} />} iconPosition="start" />
                        <Tab label="Resources" icon={<Download size={18} />} iconPosition="start" />
                    </Tabs>
                </Box>

                <Grid container spacing={4}>
                    <Grid item xs={12} lg={12}>
                        {tabValue === 1 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                {/* Subjects & Curriculum Section */}
                                <Box>
                                    {/* <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <BookOpen size={24} color="#4f46e5" /> Subjects & Curriculum
                                    </Typography> */}
                                    {(course?.subjects || subjects).map((subject: any, sIdx: number) => (
                                        <Paper key={subject.id || sIdx} variant="outlined" sx={{ 
                                            mb: 2, 
                                            p: 3,
                                            borderRadius: "16px", 
                                            border: "1px solid #e2e8f0", 
                                            display: "flex", 
                                            alignItems: "center", 
                                            justifyContent: "space-between",
                                            boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": {
                                                borderColor: "#cbd5e1",
                                                boxShadow: "0 4px 15px rgba(0,0,0,0.04)"
                                            }
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexGrow: 1 }}>
                                                <Box sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: "12px",
                                                    bgcolor: "#e0e7ff",
                                                    color: "#4f46e5",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0
                                                }}>
                                                    <BookOpen size={24} />
                                                </Box>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    {isEditing ? (
                                                        <TextField fullWidth size="small" defaultValue={subject.name} sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }} />
                                                    ) : (
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", mb: 0.5 }}>
                                                            {subject.name}
                                                        </Typography>
                                                    )}
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 3, mt: 0.5 }}>
                                                        {subject.duration ? (
                                                            <Typography variant="body2" sx={{ color: "#64748b", display: "flex", alignItems: "center", gap: 0.5, fontWeight: 500 }}>
                                                                <Clock size={16} /> Duration: {subject.duration} Weeks
                                                            </Typography>
                                                        ) : null}
                                                        {!isEditing && subject.startDate && subject.endDate ? (
                                                            <Typography variant="body2" sx={{ color: "#64748b", display: "flex", alignItems: "center", gap: 0.5, fontWeight: 500 }}>
                                                                <Calendar size={16} /> {subject.startDate} - {subject.endDate}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>


                            </Box>
                        )}

                        {tabValue === 2 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                {/* <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Download size={24} color="#4f46e5" /> Course Resources
                                </Typography> */}
                                {(course?.subjects || []).map((subject: any, sIdx: number) => (
                                    <Box key={subject.id || sIdx} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "#334155" }}>
                                            {subject.name}
                                        </Typography>
                                        {subject.resources && subject.resources.length > 0 ? (
                                            <Grid container spacing={2}>
                                                {subject.resources.map((res: any, rIdx: number) => (
                                                    <Grid item xs={12} md={6} lg={4} key={res.resource_id || rIdx}>
                                                        <Paper sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, borderRadius: "16px", border: "1px solid #f1f5f9", boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
                                                            <Box sx={{ p: 1.5, bgcolor: res.resource_type === "video" ? "#eef2ff" : "#f0fdf4", borderRadius: "12px", color: res.resource_type === "video" ? "#4f46e5" : "#16a34a" }}>
                                                                {res.resource_type === "video" ? <PlayCircle size={24} /> : <FileText size={24} />}
                                                            </Box>
                                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
                                                                    {res.video?.title || res.title || "Resource File"}
                                                                    {res.resource_type && (
                                                                        <Chip 
                                                                            label={res.resource_type.replace(/_/g, ' ')} 
                                                                            size="small" 
                                                                            sx={{ height: 20, fontSize: "11px", textTransform: "capitalize", bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600 }} 
                                                                        />
                                                                    )}
                                                                </Typography>
                                                                {res.video?.description && (
                                                                    <Typography variant="body2" noWrap sx={{ color: "#64748b", fontSize: "13px" }}>
                                                                        {res.video.description}
                                                                    </Typography>
                                                                )}
                                                                {res.video?.total_duration ? (
                                                                    <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.5 }}>
                                                                        {Math.floor(res.video.total_duration / 60)}m {res.video.total_duration % 60}s
                                                                    </Typography>
                                                                ) : null}
                                                            </Box>
                                                        </Paper>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic", bgcolor: "#f8fafc", p: 2, borderRadius: "12px", border: "1px dashed #cbd5e1" }}>
                                                No resources available for this subject.
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {tabValue === 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Accordion defaultExpanded sx={{ borderRadius: "24px !important", border: "1px solid #f1f5f9", boxShadow: "none", "&:before": { display: "none" } }}>
                                    <AccordionSummary expandIcon={<ChevronRight size={18} />} sx={{ px: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Info size={20} color="#4f46e5" /> Basic Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 3, pb: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Course Title</Typography>
                                                {isEditing ? (
                                                    <TextField fullWidth size="small" defaultValue={courseInfo.title} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "14px" }}>{courseInfo.title}</Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Category</Typography>
                                                {isEditing ? (
                                                    <FormControl fullWidth size="small">
                                                        <Select defaultValue={courseInfo.category} sx={{ borderRadius: "12px" }}>
                                                            <MenuItem value={courseInfo.category}>{courseInfo.category}</MenuItem>
                                                            <MenuItem value="Science">Science</MenuItem>
                                                            <MenuItem value="History">History</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "14px" }}>{courseInfo.category}</Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Year Group</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "14px" }}>{courseInfo.yeargroup}</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Region & State</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "14px" }}>{courseInfo.region} {courseInfo.state ? `(${courseInfo.state})` : ""}</Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Description</Typography>
                                                {isEditing ? (
                                                    <TextField fullWidth multiline rows={3} defaultValue={courseInfo.description} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6, fontSize: "14px" }}>{courseInfo.description}</Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion defaultExpanded sx={{ borderRadius: "24px !important", border: "1px solid #f1f5f9", boxShadow: "none", "&:before": { display: "none" } }}>
                                    <AccordionSummary expandIcon={<ChevronRight size={18} />} sx={{ px: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Calendar size={20} color="#4f46e5" /> Course Duration
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 3, pb: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Duration</Typography>
                                                {isEditing ? (
                                                    <TextField fullWidth type="number" defaultValue={courseInfo.duration} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "14px" }}>
                                                        {(() => {
                                                            const d = Number(courseInfo.duration) || 0;
                                                            const weeks = Math.floor(d / 7);
                                                            const days = d % 7;
                                                            const parts = [];
                                                            if (weeks > 0) parts.push(`${weeks} ${weeks === 1 ? 'Week' : 'Weeks'}`);
                                                            if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
                                                            return parts.length > 0 ? parts.join(' ') : '0 Days';
                                                        })()}
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </motion.div>
        </Box>
    );
};

export default CourseDetail;
